import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import "./HomeHero.css";

const HomeHero = () => {
  const navigate = useNavigate();
  const [activeVideoOne, setActiveVideoOne] = useState(null);
  const leftVideoRefOne = useRef(null);
  const rightVideoRefOne = useRef(null);
  const [isVideoLoadedOne, setIsVideoLoadedOne] = useState({ left: false, right: false });
  const [isPlayingOne, setIsPlayingOne] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({ left: false, right: false });
  const [isMobile, setIsMobile] = useState(false);
  const hoverTimer = useRef(null);

  const heroSectionRef = useRef(null);
  const heroTextRef = useRef(null);
  const [hideScrollElements, setHideScrollElements] = useState(false);

  // This controls whether the Header's navbar-center logo is visible
  // We pass this as a prop concept via a global CSS class on body
  const [navbarLogoVisible, setNavbarLogoVisible] = useState(false);
  const [headerSolid, setHeaderSolid] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight;
      // progress: 0 = top of hero, 1 = hero fully scrolled past
      const progress = Math.min(scrollY / heroHeight, 1);

      // --- VOLUMAIRE text animation ---
      // Phase 1 (0 → 0.7): text scales down from 1 → ~0.12 and moves toward navbar center
      // Phase 2 (0.7 → 1): text is fully in navbar, hidden (navbar takes over)

      const animationEnd = 0.75; // point at which text reaches navbar

      if (progress < animationEnd) {
        const t = progress / animationEnd; // 0 → 1 over first 75% of scroll

        // Scale: 1 → 0 (we'll use a small target so it "merges" into navbar)
        // The navbar logo is ~40px font, hero is 110px → ratio ~0.36
        // But we scale the whole element, so target scale ≈ 0.13
        const scale = 1 - t * 0.87; // 1 → 0.13

        // Vertical: move from center (-50% of viewport) downward
        // At t=1 it should be at navbar height (~39px from top = announcement+navbar/2)
        // center of viewport = heroHeight/2 from top
        // navbar center ≈ 78px/2 = 39px from top (announcement hidden at this point)
        // so translateY needed = (heroHeight/2 - 39) * t (move UP from center toward top)
        const centerY = heroHeight / 2;
        const navbarCenterY = 39; // approx center of navbar
        const moveUp = (centerY - navbarCenterY) * t;

        if (heroTextRef.current) {
          heroTextRef.current.style.opacity = "1";
          heroTextRef.current.style.transform = `translate(-50%, calc(-50% - ${moveUp}px)) scale(${scale})`;
        }

        setNavbarLogoVisible(false);
        setHeaderSolid(false);
      } else {
        // Text has merged into navbar — hide hero text, show navbar logo
        if (heroTextRef.current) {
          heroTextRef.current.style.opacity = "0";
          heroTextRef.current.style.transform = `translate(-50%, calc(-50% - ${(heroHeight / 2 - 39)}px)) scale(0.13)`;
        }
        setNavbarLogoVisible(true);
        setHeaderSolid(true);
      }

      // Hide scroll text and arrow after small scroll
      setHideScrollElements(progress > 0.1);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("scroll", handleScroll);
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    };
  }, []);

  // Sync navbar solid state with body class so Header can read it
  useEffect(() => {
    if (headerSolid) {
      document.body.classList.add("hero-scrolled-solid");
    } else {
      document.body.classList.remove("hero-scrolled-solid");
    }
    return () => {
      document.body.classList.remove("hero-scrolled-solid");
    };
  }, [headerSolid]);

  // Sync navbar logo visibility
  useEffect(() => {
    if (navbarLogoVisible) {
      document.body.classList.add("hero-logo-in-navbar");
    } else {
      document.body.classList.remove("hero-logo-in-navbar");
    }
    return () => {
      document.body.classList.remove("hero-logo-in-navbar");
    };
  }, [navbarLogoVisible]);

  useEffect(() => {
    leftVideoRefOne.current?.load();
    rightVideoRefOne.current?.load();
  }, []);

  useEffect(() => {
    const loadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = resolve;
      });
    };

    Promise.all([
      loadImage("/assets/headerimage2.jpg"),
      loadImage("/assets/fitness4.jpg")
    ]).catch(() => {});
  }, []);

  const safePlayVideoOne = async (video) => {
    if (!video) return;
    try {
      video.currentTime = 0;
      await video.play();
    } catch (err) {
      if (err.name !== "AbortError") console.warn("Video play error:", err);
    }
  };

  const handleImageMouseEnterOne = (side) => {
    if (isMobile) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setActiveVideoOne(side);
    setIsPlayingOne(true);
    const video = side === "left" ? leftVideoRefOne.current : rightVideoRefOne.current;
    safePlayVideoOne(video);
  };

  const handleImageMouseLeaveOne = (side) => {
    if (isMobile) return;
    hoverTimer.current = setTimeout(() => {
      if (activeVideoOne === side && !isPlayingOne) handleHideVideoOne();
    }, 100);
  };

  const handleVideoMouseEnterOne = () => {
    if (isMobile) return;
    setIsPlayingOne(true);
  };

  const handleVideoMouseLeaveOne = () => {
    if (isMobile) return;
    setIsPlayingOne(false);
    handleHideVideoOne();
  };

  const handleHideVideoOne = () => {
    setActiveVideoOne(null);
    setIsPlayingOne(false);
    [leftVideoRefOne.current, rightVideoRefOne.current].forEach((v) => {
      if (!v) return;
      try { v.pause(); v.currentTime = 0; } catch (err) {}
    });
  };

  const handleVideoLoadedOne = (side) => {
    setIsVideoLoadedOne((prev) => ({ ...prev, [side]: true }));
  };

  const handleVideoErrorOne = () => {};

  const handleImageLoad = (side) => {
    setImagesLoaded(prev => ({ ...prev, [side]: true }));
  };

  const handleTouchStart = (side) => {
    if (!isMobile) return;
    setActiveVideoOne(side);
    const video = side === "left" ? leftVideoRefOne.current : rightVideoRefOne.current;
    if (video) { video.currentTime = 0; video.play().catch(() => {}); }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setTimeout(handleHideVideoOne, 300);
  };

  const handleImageClick = (side) => {
    if (!isMobile) return;
    setActiveVideoOne(prev => prev === side ? null : side);
    const video = side === "left" ? leftVideoRefOne.current : rightVideoRefOne.current;
    if (video) {
      if (activeVideoOne === side) { video.pause(); video.currentTime = 0; }
      else { video.currentTime = 0; video.play().catch(() => {}); }
    }
  };

  const handleBathBodyClick = () => {
    navigate("/products?category=bath-body");
  };

  const handleScrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <section ref={heroSectionRef} className="home-hero-one">
      <Header forceSolid={headerSolid} />

      <div
        className={`fullscreen-video-background-one ${activeVideoOne ? "active-one" : ""}`}
        onMouseEnter={handleVideoMouseEnterOne}
        onMouseLeave={handleVideoMouseLeaveOne}
        onTouchEnd={handleTouchEnd}
      >
        <div className={`video-layer-one ${activeVideoOne === "left" ? "active-one" : ""}`}>
          <video
            ref={leftVideoRefOne}
            className="hero-background-video-one"
            muted
            loop
            playsInline
            preload="metadata"
            onLoadedData={() => handleVideoLoadedOne("left")}
            onError={handleVideoErrorOne}
          >
            <source src="/videos/beauty.mp4" type="video/mp4" />
          </video>
        </div>
        <div className={`video-layer-one ${activeVideoOne === "right" ? "active-one" : ""}`}>
          <video
            ref={rightVideoRefOne}
            className="hero-background-video-one"
            muted
            loop
            playsInline
            preload="metadata"
            onLoadedData={() => handleVideoLoadedOne("right")}
            onError={handleVideoErrorOne}
          >
            <source src="/videos/healthandfitness.mp4" type="video/mp4" />
          </video>
        </div>
      </div>

      <div className={`hero-images-one ${activeVideoOne ? "hidden-one" : ""}`}>
        <div
          className="hero-image-container-one hero-image-left-one"
          onMouseEnter={() => handleImageMouseEnterOne("left")}
          onMouseLeave={() => handleImageMouseLeaveOne("left")}
          onTouchStart={() => handleTouchStart("left")}
          onTouchEnd={handleTouchEnd}
          onClick={() => handleImageClick("left")}
        >
          <div className="image-wrapper-one">
            <img
              src="/assets/headerimage2.jpg"
              alt="Beauty"
              className={`hero-image-one ${imagesLoaded.left ? 'loaded-one' : ''}`}
              onLoad={() => handleImageLoad("left")}
            />
            <div className="image-overlay-one"></div>
          </div>
        </div>
        <div
          className="hero-image-container-one hero-image-right-one"
          onMouseEnter={() => handleImageMouseEnterOne("right")}
          onMouseLeave={() => handleImageMouseLeaveOne("right")}
          onTouchStart={() => handleTouchStart("right")}
          onTouchEnd={handleTouchEnd}
          onClick={() => handleImageClick("right")}
        >
          <div className="image-wrapper-one">
            <img
              src="/assets/fitness4.jpg"
              alt="Health & Fitness"
              className={`hero-image-one ${imagesLoaded.right ? 'loaded-one' : ''}`}
              onLoad={() => handleImageLoad("right")}
            />
            <div className="image-overlay-one"></div>
          </div>
        </div>
      </div>

      <div className="hero-center-one">
        {/* VOLUMAIRE hero text — animates into navbar on scroll */}
        <h1
          ref={heroTextRef}
          className="volumair-logo-text-one"
          style={{ transition: "none" }}
        >
          VOLUMAIRE
        </h1>

        <div className={`hero-scroll-section ${activeVideoOne ? "hidden-one" : ""}`}>
          <div className={`hero-scroll-elements ${hideScrollElements ? 'hidden-elements' : ''}`}>
            <div
              className="hero-scroll-text-wrapper clickable"
              onClick={handleBathBodyClick}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBathBodyClick(); }}
            >
              <div className="hero-scroll-text">WHERE SKINCARE BEGINS</div>
              <div className="underline-container">
                <div className="underline-base"></div>
                <div className="underline-animated"></div>
              </div>
            </div>
            <div className="hero-down-arrow">
              <button className="hero-down-arrow-btn" aria-label="Scroll down" onClick={handleScrollDown}>
                <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M6 9L12 15L18 9" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;